import React from 'react';
import { TooltipRenderProps } from 'react-joyride';
import { Button } from '@/components/ui/button';
import { X, ChevronLeft, ChevronRight, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import logoImage from '@/assets/chopperswap-logo-no-bg.png';

export const TutorialTooltip: React.FC<TooltipRenderProps> = ({
  continuous,
  index,
  step,
  backProps,
  closeProps,
  primaryProps,
  skipProps,
  tooltipProps,
  size,
  isLastStep,
}) => {
  const progress = ((index + 1) / size) * 100;

  return (
    <div
      {...tooltipProps}
      className={cn(
        "relative rounded-xl overflow-hidden",
        "bg-white dark:bg-slate-900",
        "shadow-[0_20px_60px_-15px_rgba(0,0,0,0.4)]",
        "border-2 border-slate-200 dark:border-slate-700",
        "animate-in fade-in-0 zoom-in-95 slide-in-from-bottom-2 duration-300",
        "w-[420px] max-w-[calc(100vw-32px)]"
      )}
    >
      {/* Accent bar at top */}
      <div className="h-1.5 bg-gradient-to-r from-primary via-primary/80 to-primary/60" />

      {/* Header */}
      <div className="flex items-center justify-between px-5 pt-4 pb-2">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[hsl(205,87%,21%)] to-[hsl(205,87%,35%)] flex items-center justify-center shadow-md p-1">
            <img 
              src={logoImage} 
              alt="ChopperSwap" 
              className="w-full h-full object-contain brightness-0 invert"
            />
          </div>
          <span className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
            Guided Tour
          </span>
        </div>
        <button
          {...closeProps}
          className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label="Close tutorial"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Content */}
      <div className="px-5 py-3">
        {step.title && (
          <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2 leading-tight">
            {step.title}
          </h3>
        )}
        <p className="text-base text-slate-600 dark:text-slate-300 leading-relaxed">
          {step.content}
        </p>
      </div>

      {/* Progress indicator */}
      <div className="flex justify-center gap-2 px-5 pb-4 items-center">
        {size <= 6 ? (
          Array.from({ length: size }).map((_, i) => (
            <div
              key={i}
              className={cn(
                "h-2 rounded-full transition-all duration-300",
                i === index
                  ? "w-6 bg-primary"
                  : i < index
                  ? "w-2 bg-emerald-500"
                  : "w-2 bg-slate-300 dark:bg-slate-600"
              )}
            />
          ))
        ) : (
          <div className="flex items-center gap-3 w-full max-w-[280px]">
            <div className="flex-1 h-2 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
              <div
                className="h-full rounded-full bg-primary transition-all duration-500 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
            <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 whitespace-nowrap tabular-nums">
              {index + 1}/{size}
            </span>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between px-5 py-4 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-200 dark:border-slate-700">
        <Button
          {...skipProps}
          variant="ghost"
          size="sm"
          className="text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 font-medium"
        >
          Skip Tour
        </Button>

        <div className="flex items-center gap-2">
          {index > 0 && (
            <Button
              {...backProps}
              variant="outline"
              size="sm"
              className="gap-1.5 border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700"
            >
              <ChevronLeft className="w-4 h-4" />
              Back
            </Button>
          )}
          
          <Button
            {...primaryProps}
            size="sm"
            className={cn(
              "gap-1.5 font-semibold text-white transition-all duration-200",
              isLastStep
                ? "bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600 shadow-lg shadow-emerald-500/30"
                : "bg-gradient-to-r from-[hsl(205,87%,21%)] to-[hsl(205,87%,30%)] hover:from-[hsl(205,87%,25%)] hover:to-[hsl(205,87%,35%)] shadow-lg shadow-[hsl(205,87%,21%)]/30"
            )}
          >
            {isLastStep ? (
              <>
                <Check className="w-4 h-4" />
                Complete
              </>
            ) : (
              <>
                Next
                <ChevronRight className="w-4 h-4" />
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};
